// sim/discoveryExample.ts — headless Evolution-Discovery demo + the world builder.
//
// `buildDiscoveryWorld(config)` REPLICATES buildEvolutionWorld's wiring EXACTLY
// (same SoA store, grid, genomes, colonies, pathing, genes, tasks, AI, organism
// loop, resources, evolution-box — drawn from the colony/box RNG streams in the
// SAME order, so colony + box determinism stays byte-identical) and then layers
// the ADDITIVE Evolution-Discovery observer: a DiscoverySystem that watches the
// gene pools and emits emergent-morph discoveries, plus a leaderboard wired WITH
// the discovery renown source so the new Discovery dimension is populated.
//
// The discovery system runs BEFORE the leaderboard (so renown is same-tick fresh)
// and is a PURE FUNCTION of gene-pool state (no RNG) — it can never perturb the
// colony/box layers or the RNG sequence. The determinism test and the CLI both
// import this, so the demo and the test exercise ONE code path.
//
// Run it directly:  node src/sim/discoveryExample.ts --colonies 2 --ticks 300 --discovery

import {
  CASTE,
  NIL,
  DISCOVERY_STATUS,
  DISCOVERY_EVENT,
} from '../types.ts';
import type {
  ColonyWorldConfig,
  EvolutionBoxConfig,
  DiscoveryConfig,
  MutationEnergySink,
  System,
  Tick,
} from '../types.ts';
import { Rng } from '../rng.ts';
import { fnvU32, hashHex } from '../hash.ts';
import { EntitySystem } from '../engine/EntitySystem.ts';
import { WorldGrid } from '../engine/WorldGrid.ts';
import { GameLoop } from '../engine/GameLoop.ts';
import { GreedyPathing } from '../engine/Pathing.ts';
import { ResourceSystem } from './resources.ts';
import { ColonySystem } from './colony.ts';
import { OrganismSystem } from './organisms.ts';
import { TaskQueue } from './taskQueue.ts';
import type { AutonomyCtx } from './taskQueue.ts';
import { ColonyAISystem } from './colonyAI.ts';
import { GenomeSystem, baseProfile } from './genome.ts';
import { GeneInventory } from './genes.ts';
import { EvolutionBoxSystem } from './evolutionBox.ts';
import type { ColonyEnergySource } from './evolutionBox.ts';
import { DiscoverySystem } from './discovery.ts';
import {
  defaultCasteConfigs,
  defaultColonyConfig,
  defaultGeneConfig,
  defaultColonyAIConfig,
  defaultLeaderboardConfig,
  renderColony,
  AI_STREAM_SALT,
} from './colonyExample.ts';
import { LeaderboardSystem } from './leaderboard.ts';
import {
  defaultEvolutionBoxConfig,
  computeEvolutionStateHash,
} from './evolutionBoxExample.ts';
import type { EvolutionWorld } from './evolutionBoxExample.ts';
import { MORPH_NAME, MORPH_DESC, MORPH_TIER, MORPH_COUNT } from './discoveryData.ts';

// ---------------------------------------------------------------------------
// World container — a structural SUPERSET of EvolutionWorld (so the shared
// computeEvolutionStateHash accepts it) plus the Evolution-Discovery layer.
// ---------------------------------------------------------------------------

export interface DiscoveryWorld extends EvolutionWorld {
  /** The Evolution-Discovery observer (emergent-morph archives + events). */
  readonly discovery: DiscoverySystem;
}

// ---------------------------------------------------------------------------
// Default tuning (shared by the CLI and tests). EXACT values per the spec.
// ---------------------------------------------------------------------------

/** Evolution-Discovery observer defaults. All integer; tiers ascend Common→Legendary. */
export function defaultDiscoveryConfig(): DiscoveryConfig {
  return {
    cadence: 5,
    minPoolToExpress: 3,
    discoveryRenownByTier: [10, 20, 40, 80, 160], // by RARITY.* Common→Legendary
    expressRenownByTier: [1, 2, 4, 8, 16],
    eventRingCapacity: 64,
    animBaseDuration: 20,
    animDurationPerTier: 8,
  };
}

// ---------------------------------------------------------------------------
// Colony-energy seam (same as evolutionBoxExample): colony count + per-colony
// live-Flyer count, read lazily so it stays allocation-free + live.
// ---------------------------------------------------------------------------

function buildEnergySource(colonies: ColonySystem): ColonyEnergySource {
  return {
    colonyCount(): number {
      return colonies.count;
    },
    flyerCountOf(cid: number): number {
      return colonies.casteCountAt(cid, CASTE.Flyer);
    },
  };
}

// ---------------------------------------------------------------------------
// World builder. REPLICATES buildEvolutionWorld exactly (same RNG draw order for
// walls/food/colonies + the same independent box RNG seed), then adds discovery.
// ---------------------------------------------------------------------------

export function buildDiscoveryWorld(
  config: ColonyWorldConfig & { evolution: EvolutionBoxConfig; discovery: DiscoveryConfig },
): DiscoveryWorld {
  // The colony RNG stream — drawn in the SAME order as buildEvolutionWorld so the
  // colony layer is byte-identical. The box layer draws from a SEPARATE stream.
  const rng = new Rng(config.seed);

  const peakPerColony = Math.max(config.colony.maxPopulation, config.startMembers);
  const capacity = config.colonies * peakPerColony + config.food + 16;

  const entities = new EntitySystem(capacity);
  const grid = new WorldGrid(entities, config.width, config.height, config.maxPerTile);
  const resources = new ResourceSystem(entities, grid, config.resource, config.food);

  const baseProfiles = config.castes.map((cfg, caste) => baseProfile(cfg, caste === CASTE.Flyer));
  const genomes = new GenomeSystem(baseProfiles);

  const maxColonies = Math.max(1, config.colonies);
  const colonies = new ColonySystem(entities, grid, genomes, config.colony, maxColonies);
  const pathing = new GreedyPathing(grid);
  const geneConfig = config.gene ?? defaultGeneConfig();
  const genes = new GeneInventory(maxColonies, geneConfig);

  // --- Autonomy: the task scheduler + the decision engine. ---
  const aiConfig = config.ai ?? defaultColonyAIConfig();
  const tasks = new TaskQueue(maxColonies, capacity, aiConfig.tasksPerColony);
  // Per-tick AI decisions draw from a stream isolated from worldgen (DESIGN §6),
  // matching buildColonyWorld/buildEcosystemWorld — same AI_STREAM_SALT.
  const aiRng = new Rng((config.seed ^ AI_STREAM_SALT) >>> 0);
  const ai = new ColonyAISystem(
    colonies, entities, grid, aiRng, tasks, genes, aiConfig, config.colony.maxPopulation,
  );

  // --- Evolution-Box economy: an INDEPENDENT RNG stream (SAME seed derivation as
  //     buildEvolutionWorld) so box draws never perturb colony determinism. ---
  const evolutionRng = new Rng((config.seed ^ 0x85ebca6b) >>> 0);
  const energySource = buildEnergySource(colonies);
  const clock = { tick: 0 };
  const evolution = new EvolutionBoxSystem(
    genes, evolutionRng, config.evolution, energySource, maxColonies, clock,
  );

  // --- Evolution-Discovery observer: PURE function of gene-pool state (no RNG),
  //     so it draws nothing and can never perturb the colony/box determinism. ---
  const discovery = new DiscoverySystem(
    genes, { colonyCount: () => colonies.count }, config.discovery, maxColonies, clock,
  );

  // The organism loop reads the autonomy-widened ctx; ctx.mutation routes kill
  // energy into the box economy. SAME shape as buildEvolutionWorld.
  const ctx: AutonomyCtx & { readonly mutation: MutationEnergySink } = {
    entities, grid, rng, colonies, resources, genomes, pathing,
    clock, genes, geneConfig,
    // Per-creature movement speed (opt-in; mirrors buildColonyWorld). Off by default
    // ⇒ original integer stepsPerTick movement, byte-identical.
    creatureSpeed: config.colony.creatureSpeed === true,
    tasks, onExpand: ai.onExpand, onScout: ai.onScout,
    mutation: evolution,
  };
  const organisms = new OrganismSystem(ctx);

  // --- Sparse walls (~6%), deterministic; drawn first so placement avoids them.
  //     Identical to buildEvolutionWorld (same RNG draws, same order).
  const wallTarget = Math.floor(grid.size * 0.06);
  let wallsPlaced = 0;
  let guard = 0;
  const guardMax = grid.size * 4;
  while (wallsPlaced < wallTarget && guard++ < guardMax) {
    const x = rng.int(config.width);
    const y = rng.int(config.height);
    if (grid.isPassable(x, y)) {
      grid.setPassable(x, y, false);
      wallsPlaced++;
    }
  }

  // --- Food nodes on passable tiles (colony RNG). ---
  placeOnPassable(rng, grid, config.food, (x, y) => {
    resources.addFood(x, y, config.foodAmount);
  });

  // --- Colony nests + founding members on passable tiles (colony RNG). ---
  placeOnPassable(rng, grid, config.colonies, (x, y) => {
    const cid = colonies.createColony(x, y);
    colonies.seedInitialMembers(cid, config.startMembers);
  });

  // --- Fixed system order: ai → scheduler → organisms → colonies → resources →
  //     evolution → discovery → leaderboard. Discovery runs BEFORE the board so
  //     its renown is fresh same-tick; both observe only (write own stores). ---
  const scheduler: System = {
    name: 'task-scheduler',
    update(_tick: Tick): void {
      tasks.assignOpenTasks(ctx);
    },
  };

  // Leaderboard WIRED WITH the discovery renown source (the new optional dim).
  const leaderboard = new LeaderboardSystem(
    colonies, ai, genes, config.leaderboard ?? defaultLeaderboardConfig(), discovery,
  );

  const loop = new GameLoop();
  loop
    .add(ai)
    .add(scheduler)
    .add(organisms)
    .add(colonies)
    .add(resources)
    .add(evolution)
    .add(discovery)
    .add(leaderboard);

  return {
    config: config as EvolutionWorld['config'],
    rng, entities, grid, loop, resources, colonies, organisms,
    genes, genomes, tasks, ai, leaderboard,
    evolution, evolutionRng,
    discovery,
    creatureSpeed: config.colony.creatureSpeed === true,
  };
}

/** Find a passable tile (deterministic, RNG-driven) and invoke `place(x,y)`. */
function placeOnPassable(
  rng: Rng,
  grid: WorldGrid,
  count: number,
  place: (x: number, y: number) => void,
): void {
  for (let i = 0; i < count; i++) {
    let x = 0;
    let y = 0;
    let tries = 0;
    do {
      x = rng.int(grid.width);
      y = rng.int(grid.height);
      tries++;
    } while (!grid.isPassable(x, y) && tries < 64);
    if (grid.isPassable(x, y)) place(x, y);
  }
}

/** Advance the world by `ticks` ticks (alias of the evolution runner). */
export function runHeadless(world: DiscoveryWorld, ticks: number): void {
  world.loop.step(ticks);
}

// ---------------------------------------------------------------------------
// Deterministic state fingerprint (evolution state + discovery archives/events).
// ---------------------------------------------------------------------------

export function computeDiscoveryStateHash(world: DiscoveryWorld): number {
  // The evolution hash already folds the FULL colony + organism + gene + box state
  // (and the leaderboard standings, which now include the Discovery dimension).
  let h = computeEvolutionStateHash(world);

  const d = world.discovery;
  const colonies = world.colonies;

  // Per-morph archive columns, in fixed morph id order.
  for (let m = 0; m < MORPH_COUNT; m++) {
    h = fnvU32(h, d.statusOf(m));
    h = fnvU32(h, d.discovererOf(m));
    h = fnvU32(h, d.discoveryTickOf(m));
    h = fnvU32(h, d.expressersOf(m));
    h = fnvU32(h, d.peakExpressersOf(m));
    h = fnvU32(h, d.extinctionsOf(m));
    h = fnvU32(h, d.revivalsOf(m));
  }

  // Per-colony discovery columns, in fixed colony id order.
  for (let cid = 0; cid < colonies.count; cid++) {
    h = fnvU32(h, d.discoveryCreditAt(cid));
    h = fnvU32(h, d.expressRenownAt(cid));
    h = fnvU32(h, d.discoveriesMadeAt(cid));
    h = fnvU32(h, d.speciesExpressedAt(cid));
    h = fnvU32(h, d.renownAt(cid));
  }

  // Global counters.
  h = fnvU32(h, d.discoveriesTotal);
  h = fnvU32(h, d.extinctionsTotal);
  h = fnvU32(h, d.revivalsTotal);
  h = fnvU32(h, d.scansTotal);
  h = fnvU32(h, d.activeSpeciesNow);
  h = fnvU32(h, d.eventTotal());
  h = fnvU32(h, d.eventCount());

  // Each live event (chronological), all six fields.
  const ec = d.eventCount();
  for (let i = 0; i < ec; i++) {
    h = fnvU32(h, d.eventSpeciesAt(i));
    h = fnvU32(h, d.eventColonyAt(i));
    h = fnvU32(h, d.eventTickAt(i));
    h = fnvU32(h, d.eventKindAt(i));
    h = fnvU32(h, d.eventAnimStyleAt(i));
    h = fnvU32(h, d.eventAnimDurationAt(i));
  }
  return h >>> 0;
}

// ---------------------------------------------------------------------------
// Census / debug — renders the THREE ARCHIVES (recipe display gated on discovery).
// ---------------------------------------------------------------------------

/** Short label per RARITY.* tier (the morph prestige scale). */
const TIER_NAME: readonly string[] = ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'];
/** Short label per DISCOVERY_STATUS.*. */
const STATUS_NAME: readonly string[] = ['Undiscovered', 'Active', 'Extinct'];
/** Short label per DISCOVERY_EVENT.*. */
const EVENT_NAME: readonly string[] = ['Discovered', 'Extinct', 'Revived'];

/**
 * The three Evolution-Discovery archives (CLI `--discovery`). Concealment: only
 * DISCOVERED morphs are named; the rest are summarised as a hidden count, and no
 * recipe is ever printed for an undiscovered morph.
 */
export function discoveryCensus(world: DiscoveryWorld): string {
  const d = world.discovery;
  let s = '';

  // --- Species Archive: one line per DISCOVERED morph. ---
  s += '  Species Archive:\n';
  let hidden = 0;
  for (let m = 0; m < MORPH_COUNT; m++) {
    if (!d.isDiscovered(m)) {
      hidden++;
      continue;
    }
    const tier = TIER_NAME[MORPH_TIER[m] as number] as string;
    const status = STATUS_NAME[d.statusOf(m)] as string;
    const who = d.discovererOf(m);
    s +=
      `    ${(MORPH_NAME[m] as string).padEnd(20)} [${tier}] ${status} ` +
      `expressers=${d.expressersOf(m)}/${d.peakExpressersOf(m)} ` +
      `discoverer=${who === NIL ? '—' : `c${who}`}@t${d.discoveryTickOf(m)}\n`;
  }
  if (hidden > 0) s += `    … ${hidden} undiscovered species remain hidden\n`;

  // --- Extinct Branch Archive: one line per Extinct morph. ---
  s += '  Extinct Branch Archive:\n';
  let anyExtinct = false;
  for (let m = 0; m < MORPH_COUNT; m++) {
    if (d.statusOf(m) !== DISCOVERY_STATUS.Extinct) continue;
    anyExtinct = true;
    s +=
      `    ${(MORPH_NAME[m] as string).padEnd(20)} extinctions=${d.extinctionsOf(m)} ` +
      `lastExtinct=t${d.lastEventTickOf(m)} revivals=${d.revivalsOf(m)}\n`;
  }
  if (!anyExtinct) s += '    (none — no discovered branch has died out)\n';

  // --- Discovery Archive (recent): the last up-to-K events, newest first. ---
  const K = 8;
  s += '  Discovery Archive (recent):\n';
  const ec = d.eventCount();
  if (ec === 0) {
    s += '    (no events yet)\n';
  } else {
    const shown = Math.min(K, ec);
    for (let j = 0; j < shown; j++) {
      const i = ec - 1 - j; // newest-first
      const kind = EVENT_NAME[d.eventKindAt(i)] as string;
      const sp = d.eventSpeciesAt(i);
      const col = d.eventColonyAt(i);
      s +=
        `    t${d.eventTickAt(i)} ${kind.padEnd(10)} ${(MORPH_NAME[sp] as string).padEnd(20)} ` +
        `${col === NIL ? '—' : `c${col}`} ` +
        `anim(style=${d.eventAnimStyleAt(i)} dur=${d.eventAnimDurationAt(i)})\n`;
    }
  }
  return s;
}

/** One-line description of a DISCOVERED morph (concealed when undiscovered). */
export function describeMorph(world: DiscoveryWorld, m: number): string {
  const d = world.discovery;
  if (!d.isDiscovered(m)) return `(undiscovered morph #${m} — recipe concealed)`;
  return `${MORPH_NAME[m] as string}: ${MORPH_DESC[m] as string}`;
}

// ---------------------------------------------------------------------------
// CLI wrapper (mirrors evolutionBoxExample).
// ---------------------------------------------------------------------------

interface CliFlags {
  seed: number; width: number; height: number; colonies: number; members: number;
  food: number; ticks: number; every: number; quiet: boolean; map: boolean;
  discovery: boolean;
}

function parseFlags(argv: readonly string[]): CliFlags {
  const f: CliFlags = {
    seed: 1, width: 48, height: 24, colonies: 2, members: 8,
    food: 28, ticks: 300, every: 50, quiet: false, map: false,
    discovery: false,
  };
  for (let i = 0; i < argv.length; i++) {
    const a = argv[i] as string;
    switch (a) {
      case '--seed': f.seed = intArg(argv, ++i, f.seed); break;
      case '--width': f.width = intArg(argv, ++i, f.width); break;
      case '--height': f.height = intArg(argv, ++i, f.height); break;
      case '--colonies': f.colonies = intArg(argv, ++i, f.colonies); break;
      case '--members': f.members = intArg(argv, ++i, f.members); break;
      case '--food': f.food = intArg(argv, ++i, f.food); break;
      case '--ticks': f.ticks = intArg(argv, ++i, f.ticks); break;
      case '--every': f.every = intArg(argv, ++i, f.every); break;
      case '--quiet': f.quiet = true; break;
      case '--map': f.map = true; break;
      case '--discovery': f.discovery = true; break;
      default: break;
    }
  }
  return f;
}

function intArg(argv: readonly string[], i: number, fallback: number): number {
  const raw = argv[i];
  if (raw === undefined) return fallback;
  const v = Number.parseInt(raw, 10);
  return Number.isFinite(v) ? v : fallback;
}

function configFromFlags(
  f: CliFlags,
): ColonyWorldConfig & { evolution: EvolutionBoxConfig; discovery: DiscoveryConfig } {
  return {
    seed: f.seed, width: f.width, height: f.height,
    colonies: f.colonies, startMembers: f.members,
    food: f.food, foodAmount: 16, maxPerTile: 4,
    castes: defaultCasteConfigs(), colony: defaultColonyConfig(),
    resource: { regenDelay: 14, regenAmount: 16 },
    evolution: defaultEvolutionBoxConfig(),
    discovery: defaultDiscoveryConfig(),
  };
}

function runCli(argv: readonly string[]): void {
  const flags = parseFlags(argv);
  const config = configFromFlags(flags);
  const world = buildDiscoveryWorld(config);

  if (flags.quiet) {
    const start = process.hrtime.bigint();
    runHeadless(world, flags.ticks);
    const elapsedMs = Number(process.hrtime.bigint() - start) / 1e6;
    console.log(
      `quiet: colonies=${flags.colonies} ticks=${flags.ticks} grid=${flags.width}x${flags.height} | ` +
        `elapsed=${elapsedMs.toFixed(2)}ms ticks/sec=${(flags.ticks / (elapsedMs / 1000)).toFixed(0)} ` +
        `discoveries=${world.discovery.discoveriesTotal} extinct=${world.discovery.extinctionsTotal} ` +
        `revived=${world.discovery.revivalsTotal} active=${world.discovery.activeSpeciesCount()} ` +
        `hash=${hashHex(computeDiscoveryStateHash(world))}`,
    );
    return;
  }

  const small = world.grid.width <= 80 && world.grid.height <= 48;
  printFrame(world, flags, small);
  let done = 0;
  while (done < flags.ticks) {
    const chunk = Math.min(flags.every, flags.ticks - done);
    runHeadless(world, chunk);
    done += chunk;
    printFrame(world, flags, small);
  }
  console.log(
    `\nsummary: seed=${flags.seed} ticks=${world.loop.tick} ` +
      `discoveries=${world.discovery.discoveriesTotal} extinct=${world.discovery.extinctionsTotal} ` +
      `revived=${world.discovery.revivalsTotal} ` +
      `active=${world.discovery.activeSpeciesCount()} discovered=${world.discovery.discoveredCount()} ` +
      `events=${world.discovery.eventTotal()} ` +
      `finalHash=${hashHex(computeDiscoveryStateHash(world))}`,
  );
}

function printFrame(world: DiscoveryWorld, flags: CliFlags, small: boolean): void {
  console.log(
    `tick=${String(world.loop.tick).padStart(4)} ` +
      `organisms=${String(world.colonies.totalPopulation()).padStart(4)} ` +
      `discoveries=${String(world.discovery.discoveriesTotal).padStart(3)} ` +
      `active=${String(world.discovery.activeSpeciesCount()).padStart(3)} ` +
      `extinct=${String(world.discovery.extinctSpeciesCount()).padStart(3)}`,
  );
  if (flags.discovery) process.stdout.write(discoveryCensus(world));
  if (flags.map && small) process.stdout.write(renderColony(world));
}

const isMain =
  typeof process !== 'undefined' &&
  Array.isArray(process.argv) &&
  process.argv[1] !== undefined &&
  import.meta.url === `file://${process.argv[1]}`;

if (isMain) runCli(process.argv.slice(2));

// Re-export so tests/CLI can reach sentinels/counts without importing types.
export { MORPH_COUNT, NIL, DISCOVERY_STATUS, DISCOVERY_EVENT };
